import type {
  InteractionType,
} from '~/model/InteractionType';
import { VpButton, VpIcon } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useAuthenticatedContext } from '@/app/components/Provider';
import { checkIfInteractionIsComing, checkIfInteractionIsFinished } from '@/app/utils/entities/interactions';
import {
  checkIfNoPlacesLeft,
  checkIsSelection,
  checkIsSurvey,
  hasContribution,
} from '~/components/Interaction/utils';
import { interactionStatus } from '~/const/api';

export const MainCta = ({
  interaction,
  handleParticipate,
  isSticky = false,
}: {
  interaction: InteractionType;
  handleParticipate: () => void;
  isSticky?: boolean;
}) => {
  const { isAuthenticating } = useAuthenticatedContext();

  const t = useTranslations('INTERACTION');
  const isSelection = checkIsSelection(interaction);
  const isSurvey = checkIsSurvey(interaction);
  const isFinished = checkIfInteractionIsFinished(interaction);
  const isComing = checkIfInteractionIsComing(interaction);
  const hasProject = !!interaction?.project;

  const noPlacesLeft: boolean = checkIfNoPlacesLeft(interaction);
  const contributions = interaction.current_user_contribution?.count_per_status;
  const hasContributionCount = hasContribution(contributions);

  const interactionIsComingCta = isComing && (
    <span className="main-cta__non-active">
      <span>
        <VpIcon name="timer" size={20} />
      </span>
      <strong>{t('IS_COMING')}</strong>
    </span>
  );

  const primaryButtonLabel
    = (isSurvey
      && !hasProject
      && !interaction.can_have_multiple_contributions
      && interaction.result_visible_on_front
      && contributions?.submitted > 0
      && t('SEE_RESULT'))
    || t('SEE_SUBJECT');

  const interactionFinishedCta = isFinished && (
    <>
      <button
        className="main-cta__non-active"
        disabled
        type="button"
      >
        <span className="vp-body-s">
          {t('FINISHED')}
        </span>
      </button>
      {isSelection
        && interaction.status !== interactionStatus.trashed
        && hasContributionCount && (
        <VpButton
          data-cy="participate"
          data-testid="participate"
          disabled={isAuthenticating}
          shape="squared"
          onClick={handleParticipate}
        >
          {t('SEE_SUBJECT')}
        </VpButton>
      )}
    </>
  );

  const interactionTimeslotsFullCta = noPlacesLeft
    && hasContributionCount === false && (
    <span>
      <strong>{t('FULL')}</strong>
    </span>
  );

  const defaultCta = (
    <div className={isSticky ? 'interaction-aside__cta' : 'interaction__cta'}>
      <VpButton
        data-cy="participate"
        data-testid="participate"
        disabled={isAuthenticating}
        shape="squared"
        onClick={handleParticipate}
      >
        {primaryButtonLabel}
      </VpButton>
    </div>
  );

  return (
    <div className="main-cta">
      {interactionIsComingCta
        || interactionFinishedCta
        || interactionTimeslotsFullCta
        || defaultCta}
    </div>
  );
};
